import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { styles } from './../../../../styles/themes/style';
import styled from 'styled-components'
import Column from '../../../../components/Column';
import Row from '../../../../components/Row';
import SvgFragment from '../../../../fragments/SvgFragment';
import Text from '../../../../components/Text';
import { ApiKeyVisibilityToggle, maskApiKey, useCopyApiKey } from './ApiKeyVisibility';
import ApiKeyCard, { ApiKeyCardHeader, Card, LayersIcon, UserIcon, UsersIcon } from './ApiKeyCard';
import ProfilePhoto from '../../../../components/ProfilePhoto';
import { useAuthStore } from '../../../../stores/auth/useAuthStore';
import { useAuthHandlers } from '../../../../services/actions/auth/useAuthHandlers';
import { useAppHandlers } from '../../../../services/actions/app/useAppHandlers';
import { IMemberApiKey, IWorkspaceApiKeyMeta } from '../../../../interfaces/models/workspace';


interface IApiKeyProps { }

const formatDate = (date?: string | null) => date ? moment(date).format('MMM D, YYYY') : 'Never'
// Some accounts carry the same value (often the email) in both name fields; show it once.
const memberName = (item: IMemberApiKey) => Array.from(new Set([item.firstName, item.lastName].filter(Boolean))).join(' ') || item.email || 'Unknown member'
const memberInitials = (item: IMemberApiKey) => `${item.firstName?.[0] ?? ''}${item.lastName?.[0] ?? ''}`.toUpperCase()
const isActive = (item: IMemberApiKey) => !item.revokedAt && item.isMember !== false

/** "Alex R." for who generated the workspace key. */
const creatorName = (createdBy?: IWorkspaceApiKeyMeta['createdBy']) => {
    if (!createdBy) return null
    const { firstName, lastName, email } = createdBy
    if (firstName && lastName && lastName !== firstName) return `${firstName} ${lastName[0]}.`
    return firstName || lastName || email
}

const ApiKey: FunctionComponent<IApiKeyProps> = () => {

    const { store: { user, workspace, workspaceOwner, workspaceAdmin } } = useAuthStore()
    const {
        generateAPIKeyHandler, revokeAPIKeyHandler, getAPIKeysHandler,
        generateMemberAPIKeyHandler, revokeMemberAPIKeyHandler, deleteMemberAPIKeyHandler
    } = useAuthHandlers()
    const { setDestructiveModal, hideModal } = useAppHandlers()
    const copyApiKey = useCopyApiKey()

    const isAdmin = workspaceOwner || workspaceAdmin

    const [APIKey, setAPIKey] = useState('')
    const [apiKeyMeta, setApiKeyMeta] = useState<IWorkspaceApiKeyMeta | null>(null)
    const [memberApiKeys, setMemberApiKeys] = useState<IMemberApiKey[]>([])
    const [revealed, setRevealed] = useState<Record<string, boolean>>({})

    const loadKeys = useCallback(async () => {
        const result = await getAPIKeysHandler()
        if (!result) return
        if (isAdmin) {
            setAPIKey(result.apiKey ?? '')
            setApiKeyMeta(result.apiKeyMeta ?? null)
        }
        setMemberApiKeys(result.memberApiKeys ?? [])
    }, [isAdmin])

    useEffect(() => {
        loadKeys()
    }, [workspace?._id, isAdmin])

    const confirm = (headingText: string, descriptionText: string, confirmBtnTitle: string, action: () => Promise<void>) => {
        setDestructiveModal({
            headingText,
            descriptionText,
            confirmBtnTitle,
            onCancelHandler: () => hideModal(),
            onConfirmHandler: async () => {
                hideModal()
                await action()
            }
        })
    }

    /* Workspace key: owner and admins. */

    const generateWorkspaceKey = async () => {
        // Reloaded rather than set, so created / last used come along.
        if (await generateAPIKeyHandler()) loadKeys()
    }

    const revokeWorkspaceKey = () => confirm(
        'Revoke workspace API key?',
        'Integrations using this key stop working, and connected MCP clients are disconnected.',
        'Revoke key',
        async () => {
            await revokeAPIKeyHandler()
            setAPIKey('')
            setApiKeyMeta(null)
        }
    )

    const rotateWorkspaceKey = () => confirm(
        'Rotate workspace API key?',
        'A new key replaces the current one, which stops working right away. Integrations using it need the new key.',
        'Rotate key',
        generateWorkspaceKey
    )

    /* Member keys. Everyone but the owner has their own, admins included. */

    // One key per member. Admins load every member's key, so the viewer's is picked by id.
    const ownEntry = memberApiKeys.find(item => item.userId === user?._id)
    const ownKey = ownEntry && isActive(ownEntry) ? ownEntry : undefined

    const generateOwnKey = async () => {
        // Reloaded rather than set: for admins the list holds every member's key, with names.
        if (await generateMemberAPIKeyHandler()) loadKeys()
    }

    const rotateOwnKey = () => confirm(
        'Rotate your API key?',
        'A new key replaces your current one, which stops working right away. Integrations using it need the new key.',
        'Rotate key',
        generateOwnKey
    )

    const revokeMemberKey = (item: IMemberApiKey, own: boolean) => confirm(
        own ? 'Revoke your API key?' : `Revoke API key of ${memberName(item)}?`,
        'The key stops working right away. It stays in the list until it is deleted.',
        'Revoke key',
        async () => {
            if (await revokeMemberAPIKeyHandler(item.userId)) loadKeys()
        }
    )

    const deleteMemberKey = (item: IMemberApiKey) => confirm(
        `Delete API key of ${memberName(item)}?`,
        'The key stops working and is removed from the list.',
        'Delete',
        async () => {
            if (await deleteMemberAPIKeyHandler(item.userId)) loadKeys()
        }
    )

    const sortedMemberApiKeys = useMemo(() => [...memberApiKeys].sort((a, b) =>
        Number(isActive(b)) - Number(isActive(a)) || moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()
    ), [memberApiKeys])

    const status = (item: IMemberApiKey) => {
        if (item.isMember === false) return <StatusPill $tone='muted'>Left workspace</StatusPill>
        if (item.revokedAt) return <StatusPill $tone='red'>Revoked {formatDate(item.revokedAt)}</StatusPill>
        return <StatusPill $tone='green'>Active</StatusPill>
    }

    const ownKeyCard = (
        <ApiKeyCard
            icon={<UserIcon />}
            title='Your API key'
            badge='Personal'
            description='Your own credential in this workspace. Requests made with it act as you, with your permissions.'
            apiKey={ownKey?.key ?? null}
            createdAt={ownKey?.createdAt}
            lastUsedAt={ownKey?.lastUsedAt}
            emptyText={ownEntry?.revokedAt
                ? `Your previous key was revoked on ${formatDate(ownEntry.revokedAt)}. Generate a new one to keep using the API.`
                : isAdmin
                    ? "You don't have a personal API key yet. Without one, integrations you connect use the workspace key and act as the workspace owner."
                    : "You don't have an API key yet. Generate one to use the LeadDelta API and MCP server."}
            onGenerate={generateOwnKey}
            onRotate={rotateOwnKey}
            onRevoke={() => ownKey && revokeMemberKey(ownKey, true)}
        />
    )

    return (
        <Container>
            <Column alignItems gap='12px'>
                <SvgFragment type={'integrations'} />
                <Text $heading3>LeadDelta API Key</Text>
                <Text $paragraph1 $lighter>API keys can be used for LeadDelta integration services.</Text>
            </Column>

            {isAdmin ? (
                <Content>
                    <ApiKeyCard
                        icon={<LayersIcon />}
                        title='Workspace API key'
                        badge='Owner & admins'
                        description='Shared credential managed by the workspace owner and admins. Use this key for integrations that act on behalf of the workspace.'
                        apiKey={APIKey || null}
                        createdAt={apiKeyMeta?.createdAt}
                        createdBy={creatorName(apiKeyMeta?.createdBy)}
                        lastUsedAt={apiKeyMeta?.lastUsedAt}
                        emptyText='No workspace API key yet. Generate one to connect integrations.'
                        onGenerate={generateWorkspaceKey}
                        onRotate={rotateWorkspaceKey}
                        onRevoke={revokeWorkspaceKey}
                    />

                    {!workspaceOwner && ownKeyCard}

                    <Card>
                        <ApiKeyCardHeader
                            icon={<UsersIcon />}
                            title='Member API keys'
                            badge={sortedMemberApiKeys.length ? `${sortedMemberApiKeys.filter(isActive).length} active` : undefined}
                            description='Members create their own keys on this page. Requests made with a member key act as that member, with their permissions.'
                        />
                        {sortedMemberApiKeys.length ? (
                            <TableWrapper>
                                <Table>
                                    <thead>
                                        <tr>
                                            <Th>Member</Th>
                                            <Th>Key</Th>
                                            <Th>Created</Th>
                                            <Th>Last used</Th>
                                            <Th>Status</Th>
                                            <Th $right>Actions</Th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedMemberApiKeys.map(item => (
                                            <tr key={item.userId}>
                                                <Td>
                                                    <Row gap='8px' alignItems>
                                                        <ProfilePhoto source={item.profilePicture ?? undefined} capitalLetters={memberInitials(item)} allowTooltip={false} />
                                                        <MemberInfo>
                                                            <Strong title={memberName(item)}>{memberName(item)}</Strong>
                                                            {item.email && item.email !== memberName(item) && <SubLine title={item.email}>{item.email}</SubLine>}
                                                        </MemberInfo>
                                                    </Row>
                                                </Td>
                                                <Td>
                                                    <Row gap='8px' alignItems>
                                                        <KeyText>{revealed[item.userId] ? item.key : maskApiKey(item.key, 12)}</KeyText>
                                                        <ApiKeyVisibilityToggle
                                                            visible={!!revealed[item.userId]}
                                                            onToggle={() => setRevealed({...revealed, [item.userId]: !revealed[item.userId]})}
                                                        />
                                                    </Row>
                                                </Td>
                                                <Td>{formatDate(item.createdAt)}</Td>
                                                <Td>{item.lastUsedAt ? moment(item.lastUsedAt).fromNow() : 'Never'}</Td>
                                                <Td>{status(item)}</Td>
                                                <Td $right>
                                                    <Actions gap='12px' alignItems>
                                                        <ActionLink onClick={() => copyApiKey(item.key)}>Copy</ActionLink>
                                                        {isActive(item) && <ActionLink $red onClick={() => revokeMemberKey(item, false)}>Revoke</ActionLink>}
                                                        <ActionLink $red onClick={() => deleteMemberKey(item)}>Delete</ActionLink>
                                                    </Actions>
                                                </Td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </TableWrapper>
                        ) : (
                            <EmptyList>No member has created an API key yet.</EmptyList>
                        )}
                    </Card>
                </Content>
            ) : (
                <Content>
                    {ownKeyCard}
                </Content>
            )}
        </Container>
    )
}

const Container = styled.div`
    width: 100%;
    padding: 50px 16px 16px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
`
const Content = styled.div`
    width: 100%;
    max-width: 1100px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`
const EmptyList = styled.span`
    align-self: center;
    text-align: center;
    padding: 8px 0 4px;
    font-family: NunitoSans;
    font-size: 14px;
    color: ${styles.colors.black400};
`
const TableWrapper = styled.div`
    width: 100%;
    overflow-x: auto;
`
const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
`
const Th = styled.th<{ $right?: boolean }>`
    text-align: ${({ $right }) => $right ? 'right' : 'left'};
    padding: 8px 10px;
    border-bottom: 1px solid ${styles.colors.black200};
    font-family: NunitoSans;
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${styles.colors.black300};
    white-space: nowrap;
`
const Td = styled.td<{ $right?: boolean }>`
    padding: 12px 10px;
    border-bottom: 1px solid ${styles.colors.black100};
    font-family: NunitoSans;
    font-size: 13px;
    color: ${styles.colors.black500};
    vertical-align: middle;
    white-space: nowrap;
    ${({ $right }) => $right && 'text-align: right;'}
`
/* Long names and emails are cut with an ellipsis (full value on hover) instead of widening the table. */
const MemberInfo = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    max-width: 260px;
`
const Strong = styled.span`
    font-weight: 700;
    color: ${styles.colors.black600};
    overflow: hidden;
    text-overflow: ellipsis;
`
const SubLine = styled.span`
    font-size: 11.5px;
    color: ${styles.colors.black300};
    overflow: hidden;
    text-overflow: ellipsis;
`
const KeyText = styled.code`
    font-family: monospace;
    font-size: 12px;
    color: ${styles.colors.black500};
`
const StatusPill = styled.span<{ $tone: 'green' | 'red' | 'muted' }>`
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    ${({ $tone }) => $tone === 'green' && `color: ${styles.colors.green600}; background: ${styles.colors.green200};`}
    ${({ $tone }) => $tone === 'red' && `color: ${styles.colors.red600}; background: ${styles.colors.red200};`}
    ${({ $tone }) => $tone === 'muted' && `color: ${styles.colors.black300}; background: ${styles.colors.black100};`}
`
const Actions = styled(Row)`
    justify-content: flex-end;
`
const ActionLink = styled.a<{ $red?: boolean }>`
    font-weight: 700;
    cursor: pointer;
    color: ${({ $red }) => $red ? styles.colors.red600 : styles.colors.primary600};
`
export default ApiKey
